import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTCoins } from '../../context/TCoinsContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { hasRole, ROLES } from '../../utils/roleManager';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import LogoutIcon from './LogoutIcon';
import { getFavoritesCount } from '../../utils/favoritesUtils';
import LanguageSwitcher from '../LanguageSwitcher';
import { PACKAGE_TYPES, PACKAGES } from '../../data/packageModels';
import LevelBadge from '../Marketplace/LevelBadge';
import { useStoreStats } from '../../hooks/useStoreStats';

// Video Call Icon (SVG Phone Icon)
const VideoCallIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

// FavoritesCountBadge Component for Desktop
const FavoritesCountBadge = () => {
  const { currentUser } = useAuth();
  const [favoritesCount, setFavoritesCount] = useState(0);

  useEffect(() => {
    if (currentUser) {
      const updateCount = () => {
        const count = getFavoritesCount(currentUser.uid);
        setFavoritesCount(count);
      };
      
      updateCount();
      
      // Listen for favorites updates
      window.addEventListener('favoritesUpdated', updateCount);
      
      return () => {
        window.removeEventListener('favoritesUpdated', updateCount);
      };
    }
  }, [currentUser]);

  if (!currentUser || favoritesCount === 0) return null;

  return (
    <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
      {favoritesCount > 9 ? '9+' : favoritesCount}
    </span>
  );
};

const ProfileMenuDropdown = ({ onClose }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { getTotalTCoins } = useTCoins();
  const { getStoreSubscription } = useMarketplace();
  const navigate = useNavigate();
  const [showSubMenu, setShowSubMenu] = useState(false);
  const dropdownRef = useRef(null);
  const [storePackage, setStorePackage] = useState(null);
  const [storeLevel, setStoreLevel] = useState(null);
  const [storeScore, setStoreScore] = useState(null);
  const storeStats = useStoreStats();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleLogout = async () => {
    await signOut(auth);
    onClose();
  };

  const handleMenuClick = (path) => {
    navigate(path);
    onClose();
  };

  // Load store package and level
  useEffect(() => {
    const loadStoreInfo = async () => {
      if (!currentUser?.uid) {
        setStorePackage(null);
        setStoreLevel(null);
        setStoreScore(null);
        return;
      }

      try {
        // Get storeId from users collection
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../../firebase/firebaseConfig');
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          setStorePackage(null);
          setStoreLevel(null);
          setStoreScore(null);
          return;
        }

        const userData = userSnap.data();
        const storeId = userData?.storeId;

        if (!storeId) {
          setStorePackage(null);
          setStoreLevel(null);
          setStoreScore(null);
          return;
        }

        // Get store subscription/package
        const subscription = await getStoreSubscription(storeId);
        const packageType = subscription?.packageType || PACKAGE_TYPES.FREE;
        setStorePackage(packageType);

        // Get store data for level
        const storeRef = doc(db, 'stores', storeId);
        const storeSnap = await getDoc(storeRef);
        
        if (storeSnap.exists()) {
          const storeData = storeSnap.data();
          if (storeData.reputationLevel) {
            setStoreLevel(storeData.reputationLevel);
            setStoreScore(storeData.reputationScore || null);
          }
        }
      } catch (error) {
        console.error('Error loading store info:', error);
        setStorePackage(null);
        setStoreLevel(null);
        setStoreScore(null);
      }
    };

    if (currentUser) {
      loadStoreInfo();
    }
  }, [currentUser?.uid, currentUser, getStoreSubscription]);

  const isMarketplaceUser = hasRole(currentUser, ROLES.USER) || hasRole(currentUser, ROLES.FARM);

  // Get package icon
  const getPackageIcon = (packageType) => {
    const icons = {
      [PACKAGE_TYPES.FREE]: '🆓',
      [PACKAGE_TYPES.BASIC]: '⭐',
      [PACKAGE_TYPES.PREMIUM]: '💎',
      [PACKAGE_TYPES.ENTERPRISE]: '👑'
    };
    return icons[packageType] || '🆓';
  };

  // Get package name
  const getPackageName = (packageType) => {
    return PACKAGES[packageType]?.name || 'แพ็กเกจฟรี';
  };

  return (
    <div 
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[85vh] overflow-y-auto"
    >
      {/* Header - Green theme to match marketplace and mobile menu */}
      <div className="p-4 border-b border-gray-200 mobile-menu-header-green text-white">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-full bg-white bg-opacity-30 flex items-center justify-center text-white font-bold text-lg">
            {currentUser?.displayName?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white truncate">{currentUser?.displayName || 'User'}</h3>
            <p className="text-sm text-white text-opacity-80 truncate">{currentUser?.email}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Trigger TCoinsWallet modal from parent
              window.dispatchEvent(new CustomEvent('openTCoinsWallet'));
            }}
            className="flex items-center space-x-2 text-yellow-300 hover:text-yellow-200 transition-colors cursor-pointer"
          >
            <span className="text-xl">🪙</span>
            <span className="font-bold text-lg">{getTotalTCoins().toLocaleString()}</span>
            <span className="text-sm text-white text-opacity-80">T Coins</span>
          </button>
        </div>
      </div>

      {/* Menu Items */}
      <div className="py-2">
        {/* Marketplace Section - สำหรับ USER, FARM */}
        {isMarketplaceUser && (
          <>
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {t('nav.marketplace')}
            </div>
            
            <button
              onClick={() => handleMenuClick('/marketplace/my-store')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">🏪</span>
              <span className="flex-1 font-medium">{t('nav.myStore')}</span>
              <div className="flex items-center gap-2">
                {storePackage && storePackage !== PACKAGE_TYPES.FREE && (
                  <span className="text-xs" title={getPackageName(storePackage)}>
                    {getPackageIcon(storePackage)}
                  </span>
                )}
                {storeLevel && (
                  <div className="scale-75">
                    <LevelBadge level={storeLevel} score={storeScore} type="store" />
                  </div>
                )}
              </div>
            </button>

            <button
              onClick={() => handleMenuClick('/marketplace/favorites')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800 relative"
            >
              <span className="text-xl">❤️</span>
              <span className="flex-1 font-medium">{t('marketplace.favoriteProducts', { defaultValue: 'สินค้าโปรด' })}</span>
              <FavoritesCountBadge />
            </button>

            <button
              onClick={() => handleMenuClick('/marketplace/orders')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">📦</span>
              <span className="flex-1 font-medium">{t('nav.orders')}</span>
              {storeStats.orders > 0 && (
                <span className="bg-blue-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                  {storeStats.orders > 99 ? '99+' : storeStats.orders}
                </span>
              )}
            </button>

            <button
              onClick={() => handleMenuClick('/marketplace/my-store?tab=auctions')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">🔨</span>
              <span className="flex-1 font-medium">{t('nav.auction')}</span>
              {storeStats.auctions > 0 && (
                <span className="bg-purple-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                  {storeStats.auctions > 99 ? '99+' : storeStats.auctions}
                </span>
              )}
            </button>

            <button
              onClick={() => handleMenuClick('/marketplace/my-store?tab=buyerRequests')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">📢</span>
              <span className="flex-1 font-medium">{t('nav.buyerRequest')}</span>
              {storeStats.buyerRequests > 0 && (
                <span className="bg-pink-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                  {storeStats.buyerRequests > 99 ? '99+' : storeStats.buyerRequests}
                </span>
              )}
            </button>

            <button
              onClick={() => handleMenuClick('/marketplace/my-store?tab=chat')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">💬</span>
              <span className="flex-1 font-medium">แชท</span>
              {storeStats.chats > 0 && (
                <span className="bg-green-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                  {storeStats.chats > 99 ? '99+' : storeStats.chats}
                </span>
              )}
            </button>

            <button
              onClick={() => handleMenuClick('/marketplace/my-video-calls')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <VideoCallIcon className="w-5 h-5 text-purple-600" />
              <span className="flex-1 font-medium">{t('nav.videoCalls')}</span>
              {storeStats.videoCalls > 0 && (
                <span className="bg-purple-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                  {storeStats.videoCalls > 99 ? '99+' : storeStats.videoCalls}
                </span>
              )}
            </button>

            {hasRole(currentUser, ROLES.FARM) && (
              <button
                onClick={() => handleMenuClick('/marketplace/farm-dashboard')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
              >
                <span className="text-xl">🌾</span>
                <span className="flex-1 font-medium">{t('nav.farmDashboard')}</span>
              </button>
            )}
          </>
        )}

        {/* Customer Section */}
        {hasRole(currentUser, ROLES.CUSTOMER) && (
          <>
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {t('nav.services')}
            </div>
            
            <button
              onClick={() => handleMenuClick('/trip-plans')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">🗺️</span>
              <span className="flex-1 font-medium">{t('nav.tripPlans')}</span>
            </button>

            <button
              onClick={() => handleMenuClick('/bookings')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">📋</span>
              <span className="flex-1 font-medium">{t('nav.bookingHistory')}</span>
            </button>

            <button
              onClick={() => handleMenuClick('/agoda')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">🏨</span>
              <span className="flex-1 font-medium">{t('nav.bookHotel')}</span>
            </button>
          </>
        )}

        {/* Momo Section */}
        {hasRole(currentUser, ROLES.MOMO) && (
          <>
            <button
              onClick={() => handleMenuClick('/trip-plans')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">🗺️</span>
              <span className="flex-1 font-medium">Trip Plans</span>
            </button>

            <button
              onClick={() => handleMenuClick('/momo')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">👨‍💼</span>
              <span className="flex-1 font-medium">แดชบอร์ดโม</span>
            </button>

            <button
              onClick={() => handleMenuClick('/agoda')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">🏨</span>
              <span className="flex-1 font-medium">จองที่พัก</span>
            </button>
          </>
        )}

        {/* Girl Section */}
        {hasRole(currentUser, ROLES.GIRL) && (
          <>
            <button
              onClick={() => handleMenuClick('/trip-plans')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">🗺️</span>
              <span className="flex-1 font-medium">Trip Plans</span>
            </button>

            <button
              onClick={() => handleMenuClick('/girl')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">👩</span>
              <span className="flex-1 font-medium">แดชบอร์ดน้อง</span>
            </button>

            <button
              onClick={() => handleMenuClick('/agoda')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
            >
              <span className="text-xl">🏨</span>
              <span className="flex-1 font-medium">จองที่พัก</span>
            </button>
          </>
        )}

        {/* Admin Section */}
        {hasRole(currentUser, ROLES.ADMIN) && (
          <button
            onClick={() => handleMenuClick('/admin')}
            className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
          >
            <span className="text-xl">👑</span>
            <span className="flex-1 font-medium">แอดมิน</span>
          </button>
        )}

        {/* Settings Section */}
        <div className="border-t border-gray-200 mt-2 pt-2">
          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {t('common.settings')}
          </div>

          <div className="px-4 py-2">
            <LanguageSwitcher />
          </div>

          <Link
            to="/profile/settings"
            onClick={onClose}
            className="block w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-gray-800"
          >
            <span className="text-xl">⚙️</span>
            <span className="flex-1 font-medium">{t('settings.title')}</span>
          </Link>

          <button
            onClick={handleLogout}
            className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-red-50 text-red-600 transition-colors"
          >
            <LogoutIcon className="w-5 h-5" />
            <span className="flex-1">{t('settings.logout')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileMenuDropdown;
export { VideoCallIcon };

